import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  BarChart3,
  BellRing,
  CalendarClock,
  Check,
  Clock3,
  Gauge,
  GripVertical,
  Inbox,
  LayoutDashboard,
  MessageCircle,
  PhoneCall,
  PhoneMissed,
  Plus,
  RefreshCw,
  Save,
  Server,
  Settings,
  ShieldCheck,
  Ticket,
  Timer,
  Users,
  Workflow,
  X,
  type LucideIcon,
} from 'lucide-react';
import { api } from '../../api';
import { useAuth } from '../../auth';
import { userHasRole, userRoles } from '../../roles';
import { Button, Card, EmptyState, PageHeading } from '../../components/ui';
import type { MonitoringSnapshot } from '../monitoring/MonitoringPage';
import { DashboardRoleViews } from './DashboardRoleViews';
import { DashboardAppearance } from './DashboardAppearance';
import {
  dashboardDefaults,
  dashboardWidgetCatalog,
  moveDashboardWidget,
  type DashboardWidgetKey,
  type DashboardWidgetPreference,
} from './dashboard-widgets';
import type {
  DashboardData,
  DashboardDiagnostics,
  DashboardStatusMetrics,
  DashboardStatusPeriod,
  DashboardTrendData,
  DashboardTrendPeriod,
  DashboardUser,
  ServerMetricsSnapshot,
} from './dashboard-types';
import './dashboard.css';

type DashboardState = 'loading' | 'ready' | 'empty' | 'error';
type DashboardDiagnosticsState = 'loading' | 'ready' | 'error';
type DashboardMonitoringState = 'loading' | 'ready' | 'error';
type DashboardWidgetCategory = 'Основное' | 'Работа' | 'Аналитика' | 'Система';
type DashboardRoleVariant = 'admin' | 'manager' | 'specialist' | 'client';

const TREND_OPTIONS: Array<{ value: DashboardTrendPeriod; label: string }> = [
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
  { value: '90d', label: '90 дней' },
  { value: 'month', label: 'Текущий месяц' },
  { value: 'year', label: 'Текущий год' },
];

const DASHBOARD_REFRESH_INTERVAL_MS = 30_000;
const SERVER_REFRESH_INTERVAL_MS = 5_000;

type DashboardWidgetMeta = { title: string; description: string; category: DashboardWidgetCategory; icon: LucideIcon };

const WIDGET_META: Record<DashboardWidgetKey, DashboardWidgetMeta> = {
  summary: { title: 'Ключевые показатели', description: 'Текущее состояние заявок', category: 'Основное', icon: LayoutDashboard },
  trend: { title: 'Динамика заявок', description: 'Создано и решено за период', category: 'Аналитика', icon: BarChart3 },
  statuses: { title: 'Статусы заявок', description: 'Срез по текущим статусам', category: 'Аналитика', icon: Gauge },
  services: { title: 'Очереди', description: 'Нагрузка и просрочки по направлениям', category: 'Работа', icon: Inbox },
  recent: { title: 'Мои последние заявки', description: 'Недавно обновлённые обращения', category: 'Работа', icon: Ticket },
  awaiting: { title: 'Требуют вашего ответа', description: 'Заявки, где нужна реакция', category: 'Работа', icon: MessageCircle },
  unread_contacts: { title: 'Непрочитанные диалоги', description: 'Активные каналы контакт-центра', category: 'Работа', icon: MessageCircle },
  missed_calls: { title: 'Пропущенные звонки', description: 'Новые и назначенные пропущенные', category: 'Работа', icon: PhoneMissed },
  planned: { title: 'Плановые работы', description: 'Ближайшие регламентные операции', category: 'Работа', icon: CalendarClock },
  activity: { title: 'Активность команды', description: 'События в заявках', category: 'Работа', icon: Activity },
  notices: { title: 'Важные уведомления', description: 'Контроль загрузки и SLA', category: 'Основное', icon: BellRing },
  sla: { title: 'SLA и риски', description: 'Ближайшие нарушения сроков', category: 'Аналитика', icon: Clock3 },
  workload: { title: 'Загрузка сотрудников', description: 'Активные и просроченные заявки', category: 'Аналитика', icon: Users },
  worklogs: { title: 'Трудозатраты за месяц', description: 'Учтённое время и сумма', category: 'Аналитика', icon: Timer },
  system_health: { title: 'Состояние портала', description: 'Состояние всех сервисов портала', category: 'Система', icon: ShieldCheck },
  server: { title: 'Сервер', description: 'ЦП, оперативная память, диск и сеть', category: 'Система', icon: Server },
  telephony: { title: 'Телефония', description: 'SIP, операторы и события', category: 'Система', icon: PhoneCall },
  integrations: { title: 'Интеграции', description: 'Мессенджеры, CRM и почта', category: 'Система', icon: Workflow },
};

const ROLE_WIDGET_TITLES: Partial<Record<DashboardRoleVariant, Partial<Record<DashboardWidgetKey, string>>>> = {
  admin: {
    summary: 'Заявки', notices: 'Требует внимания', services: 'Очереди', recent: 'Последние заявки', awaiting: 'Требуют ответа',
  },
  manager: {
    summary: 'Операционные показатели', notices: 'Требует решения руководителя', services: 'Очереди и направления',
    unread_contacts: 'Нагрузка по каналам', worklogs: 'Трудозатраты', planned: 'Плановые работы команды',
  },
  specialist: {
    summary: 'Мои показатели', awaiting: 'Мой фокус', sla: 'Мои сроки SLA', recent: 'Мои заявки',
    missed_calls: 'Мои обратные звонки', workload: 'Свободные заявки моих очередей', planned: 'Мои плановые работы',
    worklogs: 'Мои трудозатраты',
  },
  client: { summary: 'Мои обращения', recent: 'Последние заявки', awaiting: 'Требуют вашего ответа' },
};

function widgetMeta(role: DashboardRoleVariant, key: DashboardWidgetKey): DashboardWidgetMeta {
  const meta = WIDGET_META[key];
  return { ...meta, title: ROLE_WIDGET_TITLES[role]?.[key] ?? meta.title };
}

export type DashboardViewProps = {
  appearanceControls?: ReactNode;
  user: DashboardUser;
  state: DashboardState;
  data?: DashboardData;
  trend?: DashboardTrendData | null;
  trendPeriod: DashboardTrendPeriod;
  trendLoading: boolean;
  statusMetrics?: DashboardStatusMetrics | null;
  statusPeriod: DashboardStatusPeriod;
  statusLoading: boolean;
  enabledWidgets: DashboardWidgetKey[];
  widgetSettingsOpen: boolean;
  widgetDraft: DashboardWidgetPreference[];
  savingWidgets: boolean;
  widgetsSaved: boolean;
  widgetSaveError?: string;
  diagnostics?: DashboardDiagnostics | null;
  diagnosticsState: DashboardDiagnosticsState;
  monitoring?: MonitoringSnapshot | null;
  monitoringState: DashboardMonitoringState;
  serverMetrics?: ServerMetricsSnapshot | null;
  serverMetricsState: DashboardMonitoringState;
  errorMessage?: string;
  onNavigate: (path: string) => void;
  onRetry: () => void;
  onRetryDiagnostics: () => void;
  onRetryMonitoring: () => void;
  onRetryServerMetrics: () => void;
  onOpenWidgetSettings: () => void;
  onCloseWidgetSettings: () => void;
  onPreviewWidgetMove: (key: DashboardWidgetKey, targetKey: DashboardWidgetKey) => void;
  onCommitWidgetMove: () => void;
  onCancelWidgetMove: () => void;
  onRetryWidgetSave: () => void;
  onSaveWidgets: () => void;
  onWidgetDraftChange: (next: DashboardWidgetPreference[]) => void;
  onTrendPeriodChange: (period: DashboardTrendPeriod) => void;
  onStatusPeriodChange: (period: DashboardStatusPeriod) => void;
};

function dashboardRoleVariant(user: DashboardUser): DashboardRoleVariant {
  if (user.userType === 'CLIENT') return 'client';
  if (userHasRole(user, 'ADMIN')) return 'admin';
  if (userHasRole(user, 'MANAGER')) return 'manager';
  return 'specialist';
}

const RUSSIAN_SURNAME_ENDING = /(?:ов|ев|ёв|ин|ын|ова|ева|ёва|ина|ына|ский|цкий|ская|цкая|енко|ко|ук|юк|дзе|швили|ян)$/iu;
const RUSSIAN_PATRONYMIC_ENDING = /(?:ович|евич|ич|овна|евна|ична)$/iu;

function dashboardFirstName(fullName: string) {
  const names = fullName.trim().split(/\s+/u).filter(Boolean);
  if (names.length === 0) return 'коллега';
  if (names.length === 1) return names[0];

  const [first, second] = names;
  if (RUSSIAN_PATRONYMIC_ENDING.test(second)) return first;
  if (RUSSIAN_SURNAME_ENDING.test(first) && !RUSSIAN_SURNAME_ENDING.test(second)) return second;
  return first;
}

function dashboardHeading(user: DashboardUser) {
  const displayName = dashboardFirstName(user.fullName);
  return {
    eyebrow: 'ПЕРСОНАЛЬНАЯ ГЛАВНАЯ',
    title: `Добро пожаловать, ${displayName} 👋`,
    description: 'Важное, текущая работа и состояние сервисов — в одном месте.',
  };
}

function DashboardStateView({ state, errorMessage, onRetry }: Pick<DashboardViewProps, 'state' | 'errorMessage' | 'onRetry'>) {
  if (state === 'loading') return <Card className="dashboard-v2-state"><EmptyState icon={<RefreshCw />} title="Загружаем рабочую сводку" description="Собираем заявки, диалоги и телефонию." /></Card>;
  if (state === 'error') return <Card className="dashboard-v2-state"><EmptyState icon={<AlertTriangle />} title="Не удалось загрузить главную" description={errorMessage || 'Проверьте связь с сервером и повторите запрос.'} action={<Button variant="primary" onClick={onRetry}><RefreshCw /> Повторить</Button>} /></Card>;
  return <Card className="dashboard-v2-state"><EmptyState icon={<LayoutDashboard />} title="Рабочих данных пока нет" description="Здесь появятся заявки, события и рабочие показатели." /></Card>;
}

export function dialogFocusTarget(input: { key: string; shiftKey: boolean; activeIndex: number; total: number }): number | 'close' | 'dialog' | null {
  if (input.key === 'Escape') return 'close';
  if (input.key !== 'Tab') return null;
  if (input.total <= 0) return 'dialog';
  if (input.activeIndex < 0) return input.shiftKey ? input.total - 1 : 0;
  if (input.shiftKey && input.activeIndex === 0) return input.total - 1;
  if (!input.shiftKey && input.activeIndex === input.total - 1) return 0;
  return null;
}

function WidgetSettings({ user, draft, saving, errorMessage, onClose, onSave, onChange }: {
  user: DashboardUser;
  draft: DashboardWidgetPreference[];
  saving: boolean;
  errorMessage?: string;
  onClose: () => void;
  onSave: () => void;
  onChange: (next: DashboardWidgetPreference[]) => void;
}) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const [draggedKey, setDraggedKey] = useState<DashboardWidgetKey | null>(null);
  const [dropKey, setDropKey] = useState<DashboardWidgetKey | null>(null);
  onCloseRef.current = onClose;
  const catalog = dashboardWidgetCatalog(user.userType, user.roles ?? user.role, user.role);
  const role = dashboardRoleVariant(user);
  const enabledItems = draft.filter(item => item.enabled);
  const availableItems = draft.filter(item => !item.enabled);
  const apply = (keys: DashboardWidgetKey[]) => {
    const next = new Set(keys);
    onChange(catalog.map(key => ({ key, enabled: next.has(key) })));
  };
  const toggle = (key: DashboardWidgetKey, checked: boolean) => onChange(
    draft.map(item => item.key === key ? { ...item, enabled: checked } : item),
  );
  const moveTo = (key: DashboardWidgetKey, targetKey: DashboardWidgetKey) => {
    const targetIndex = draft.findIndex(item => item.key === targetKey);
    if (targetIndex >= 0 && key !== targetKey) onChange(moveDashboardWidget(draft, key, targetIndex));
  };
  const moveBy = (key: DashboardWidgetKey, offset: -1 | 1) => {
    const ordered = draft.filter(item => item.enabled);
    const index = ordered.findIndex(item => item.key === key);
    const target = ordered[index + offset];
    if (target) moveTo(key, target.key);
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusable = () => Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    )).filter(element => !element.hasAttribute('hidden'));
    (focusable()[0] || dialog).focus();

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      const elements = focusable();
      const activeIndex = elements.indexOf(document.activeElement as HTMLElement);
      const target = dialogFocusTarget({ key: event.key, shiftKey: event.shiftKey, activeIndex, total: elements.length });
      if (target === null) return;
      event.preventDefault();
      event.stopPropagation();
      if (target === 'close') onCloseRef.current();
      else if (target === 'dialog') dialog.focus();
      else elements[target]?.focus();
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      opener?.focus();
    };
  }, []);

  return <div className="dashboard-v2-modal-backdrop" onMouseDown={event => { if (event.currentTarget === event.target) onClose(); }}>
    <section ref={dialogRef} className="dashboard-v2-modal" role="dialog" aria-modal="true" aria-labelledby="dashboard-widget-title" tabIndex={-1}>
      <header><div><span>ПЕРСОНАЛЬНАЯ ГЛАВНАЯ</span><h2 id="dashboard-widget-title">Настройка виджетов</h2><p>Выбрано: {enabledItems.length} из {catalog.length}</p></div><Button variant="ghost" size="compact" aria-label="Закрыть настройки" onClick={onClose}><X /></Button></header>
      <div className="dashboard-v2-modal__presets">
        <Button size="compact" onClick={() => apply(dashboardDefaults(user.userType, user.roles ?? user.role, user.role))}>Ролевой набор</Button>
        <Button size="compact" onClick={() => apply(catalog)}>Включить все</Button>
        <Button size="compact" onClick={() => apply([])}>Выключить все</Button>
      </div>
      <div className="dashboard-v2-modal__content">
        <section className="dashboard-v2-modal__section" aria-labelledby="dashboard-enabled-widgets">
          <div className="dashboard-v2-modal__section-head"><div><h3 id="dashboard-enabled-widgets">На главной</h3><p>Перетащите карточки или используйте стрелки, чтобы изменить порядок.</p></div><strong>{enabledItems.length}</strong></div>
          <div className="dashboard-v2-widget-list" data-widget-list="enabled">
            {enabledItems.map((item, index) => {
              const meta = widgetMeta(role, item.key);
              const Icon = meta.icon;
              return <article
                className={`dashboard-v2-widget-option${dropKey === item.key ? ' is-drop-target' : ''}`}
                data-widget-setting-key={item.key}
                data-widget-category={meta.category}
                draggable
                key={item.key}
                onDragStart={() => { setDraggedKey(item.key); setDropKey(item.key); }}
                onDragEnd={() => { setDraggedKey(null); setDropKey(null); }}
                onDragOver={event => { event.preventDefault(); setDropKey(item.key); }}
                onDrop={event => { event.preventDefault(); if (draggedKey) moveTo(draggedKey, item.key); setDraggedKey(null); setDropKey(null); }}
              >
                <span className="dashboard-v2-widget-option__index">{String(index + 1).padStart(2, '0')}</span>
                <span className="dashboard-v2-widget-option__icon" aria-hidden="true"><Icon /></span>
                <span className="dashboard-v2-widget-option__copy"><b>{meta.title}</b><small>{meta.description}</small><em>{meta.category}</em></span>
                <span className="dashboard-v2-widget-option__actions">
                  <button type="button" className="dashboard-v2-widget-option__grip" aria-label={`Перетащить ${meta.title}`} title="Перетащить"><GripVertical /></button>
                  <button type="button" aria-label={`Переместить ${meta.title} выше`} disabled={index === 0} onClick={() => moveBy(item.key, -1)}><ArrowUp /></button>
                  <button type="button" aria-label={`Переместить ${meta.title} ниже`} disabled={index === enabledItems.length - 1} onClick={() => moveBy(item.key, 1)}><ArrowDown /></button>
                  <label className="dashboard-v2-widget-check"><input type="checkbox" checked onChange={event => toggle(item.key, event.target.checked)} /><span aria-hidden="true"><Check /></span><span className="sr-only">Показывать {meta.title}</span></label>
                </span>
              </article>;
            })}
            {!enabledItems.length && <div className="dashboard-v2-widget-list__empty"><LayoutDashboard/><strong>Главная пока пустая</strong><span>Добавьте нужные карточки из списка ниже.</span></div>}
          </div>
        </section>
        <section className="dashboard-v2-modal__section" aria-labelledby="dashboard-available-widgets">
          <div className="dashboard-v2-modal__section-head"><div><h3 id="dashboard-available-widgets">Доступные виджеты</h3><p>Карточки, которые сейчас скрыты с главной.</p></div><strong>{availableItems.length}</strong></div>
          <div className="dashboard-v2-widget-list is-available" data-widget-list="available">
            {availableItems.map(item => {
              const meta = widgetMeta(role, item.key);
              const Icon = meta.icon;
              return <article className="dashboard-v2-widget-option is-available" data-widget-setting-key={item.key} data-widget-category={meta.category} key={item.key}>
                <span className="dashboard-v2-widget-option__icon" aria-hidden="true"><Icon /></span>
                <span className="dashboard-v2-widget-option__copy"><b>{meta.title}</b><small>{meta.description}</small><em>{meta.category}</em></span>
                <Button size="compact" onClick={() => toggle(item.key, true)}><Plus /> Добавить</Button>
              </article>;
            })}
            {!availableItems.length && <div className="dashboard-v2-widget-list__empty is-compact"><Check/><strong>Все доступные виджеты уже на главной</strong></div>}
          </div>
        </section>
      </div>
      {errorMessage && <div className="dashboard-v2-modal__error" role="alert"><AlertTriangle />{errorMessage}</div>}
      <footer><span>{enabledItems.length} из {catalog.length} на главной</span><div><Button variant="ghost" onClick={onClose}>Отмена</Button><Button variant="primary" loading={saving} onClick={onSave}><Save /> Сохранить главную</Button></div></footer>
    </section>
  </div>;
}

export function DashboardView(props: DashboardViewProps) {
  const {
    user, state, data, trend, trendPeriod, trendLoading, enabledWidgets,
    widgetSettingsOpen, widgetDraft, savingWidgets, widgetsSaved, widgetSaveError, diagnostics, diagnosticsState,
    monitoring, monitoringState, serverMetrics, serverMetricsState, statusMetrics, statusPeriod, statusLoading,
    errorMessage, onNavigate, onRetry, onRetryDiagnostics, onOpenWidgetSettings,
    onRetryMonitoring, onRetryServerMetrics, onCloseWidgetSettings, onSaveWidgets, onWidgetDraftChange, onTrendPeriodChange, onStatusPeriodChange,
    onPreviewWidgetMove, onCommitWidgetMove, onCancelWidgetMove, onRetryWidgetSave,
  } = props;
  const roleVariant = dashboardRoleVariant(user);
  const heading = dashboardHeading(user);

  if (state !== 'ready' || !data) {
    return <div className={`dashboard-v2 dashboard-role-dashboard dashboard-role-dashboard--${roleVariant}`} data-dashboard-role={roleVariant}>
      <PageHeading eyebrow={heading.eyebrow} title={heading.title} description={heading.description} actions={props.appearanceControls} />
      <DashboardStateView state={state} errorMessage={errorMessage} onRetry={onRetry} />
    </div>;
  }

  return <div className={`dashboard-v2 dashboard-role-dashboard dashboard-role-dashboard--${roleVariant}`} data-dashboard-role={roleVariant}>
    <PageHeading
      eyebrow={heading.eyebrow}
      title={heading.title}
      description={heading.description}
      actions={<>
        {props.appearanceControls}
        {(savingWidgets || widgetsSaved || widgetSaveError) && <span className={`dashboard-layout-save-state${widgetSaveError ? ' is-error' : savingWidgets ? ' is-saving' : ' is-saved'}`} role={widgetSaveError ? 'alert' : 'status'} aria-live="polite">
          {widgetSaveError
            ? <><AlertTriangle/> Не удалось сохранить расположение <button type="button" onClick={onRetryWidgetSave}>Повторить</button></>
            : savingWidgets ? <><RefreshCw/> Сохраняем расположение…</> : <><Check/> Расположение сохранено</>}
        </span>}
        <Button onClick={onOpenWidgetSettings}><Settings /> Настроить</Button>
        <Button variant="primary" onClick={() => onNavigate('/tickets?create=1')}><Plus /> Новая заявка</Button>
      </>}
    />
    <DashboardRoleViews
      role={roleVariant}
      data={data}
      trend={trend}
      trendPeriod={trendPeriod}
      trendLoading={trendLoading}
      statusMetrics={statusMetrics}
      statusPeriod={statusPeriod}
      statusLoading={statusLoading}
      enabledWidgets={enabledWidgets}
      layoutSaving={savingWidgets}
      onPreviewWidgetMove={onPreviewWidgetMove}
      onCommitWidgetMove={onCommitWidgetMove}
      onCancelWidgetMove={onCancelWidgetMove}
      diagnostics={diagnostics}
      diagnosticsState={diagnosticsState}
      monitoring={monitoring}
      monitoringState={monitoringState}
      serverMetrics={serverMetrics}
      serverMetricsState={serverMetricsState}
      onNavigate={onNavigate}
      onRetryDiagnostics={onRetryDiagnostics}
      onRetryMonitoring={onRetryMonitoring}
      onRetryServerMetrics={onRetryServerMetrics}
      onTrendPeriodChange={onTrendPeriodChange}
      onStatusPeriodChange={onStatusPeriodChange}
    />
    {widgetSettingsOpen && <WidgetSettings user={user} draft={widgetDraft} saving={savingWidgets} errorMessage={widgetSaveError} onClose={onCloseWidgetSettings} onSave={onSaveWidgets} onChange={onWidgetDraftChange} />}
  </div>;
}

export function DashboardPage() {
  const { user, refresh: refreshSession } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [trendPeriod, setTrendPeriod] = useState<DashboardTrendPeriod>(() => {
    if (typeof window === 'undefined') return '7d';
    const saved = window.localStorage.getItem('intez-dashboard-trend-period') as DashboardTrendPeriod | null;
    return TREND_OPTIONS.some(option => option.value === saved) ? saved! : '7d';
  });
  const [statusPeriod, setStatusPeriod] = useState<DashboardStatusPeriod>('today');
  const [widgetSettingsOpen, setWidgetSettingsOpen] = useState(false);
  const [widgetDraft, setWidgetDraft] = useState<DashboardWidgetPreference[]>([]);
  const widgetDraftRef = useRef<DashboardWidgetPreference[]>([]);
  const replaceWidgetDraft = (next: DashboardWidgetPreference[]) => {
    widgetDraftRef.current = next;
    setWidgetDraft(next);
  };
  const widgetQueryKey = ['dashboard-widgets', user?.id ?? 'anonymous', userRoles(user).join(',')] as const;

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  const widgetsQuery = useQuery({
    queryKey: widgetQueryKey,
    queryFn: () => api<DashboardWidgetPreference[]>('/dashboard/widgets'),
  });
  const overviewQuery = useQuery({
    queryKey: ['dashboard'],
    queryFn: ({ signal }) => api<DashboardData>('/dashboard/overview', { signal }),
    refetchInterval: DASHBOARD_REFRESH_INTERVAL_MS,
  });
  const trendEnabled = Boolean(widgetsQuery.data?.find(item => item.key === 'trend')?.enabled);
  const trendQuery = useQuery({
    queryKey: ['dashboard-trend', trendPeriod],
    queryFn: ({ signal }) => api<DashboardTrendData>(`/dashboard/trend?period=${trendPeriod}`, { signal }),
    placeholderData: previous => previous,
    enabled: trendEnabled,
    refetchInterval: DASHBOARD_REFRESH_INTERVAL_MS,
  });
  const enabledWidgetKeys = new Set(
    widgetsQuery.data?.filter(item => item.enabled).map(item => item.key) ?? [],
  );
  const statusMetricsEnabled = enabledWidgetKeys.has('statuses');
  const statusMetricsQuery = useQuery({
    queryKey: ['dashboard-statuses', statusPeriod],
    queryFn: ({ signal }) => api<DashboardStatusMetrics>(`/dashboard/statuses?period=${statusPeriod}`, { signal }),
    placeholderData: previous => previous,
    enabled: statusMetricsEnabled,
    refetchInterval: DASHBOARD_REFRESH_INTERVAL_MS,
  });
  const diagnosticsEnabled = userHasRole(user, 'ADMIN')
    && ['system_health', 'telephony', 'notices'].some(key => enabledWidgetKeys.has(key as DashboardWidgetKey));
  const monitoringEnabled = userHasRole(user, 'ADMIN')
    && ['system_health', 'notices', 'integrations'].some(key => enabledWidgetKeys.has(key as DashboardWidgetKey));
  const serverMetricsEnabled = userHasRole(user, 'ADMIN') && enabledWidgetKeys.has('server');
  const diagnosticsQuery = useQuery({
    queryKey: ['telephony-browser-diagnostics'],
    queryFn: () => api<DashboardDiagnostics>('/telephony/browser/diagnostics'),
    enabled: diagnosticsEnabled,
    retry: false,
    refetchInterval: DASHBOARD_REFRESH_INTERVAL_MS,
  });
  const monitoringQuery = useQuery({
    queryKey: ['monitoring-snapshot'],
    queryFn: () => api<MonitoringSnapshot>('/monitoring'),
    enabled: monitoringEnabled,
    retry: false,
    refetchInterval: DASHBOARD_REFRESH_INTERVAL_MS,
  });
  const serverMetricsQuery = useQuery({
    queryKey: ['server-metrics'],
    queryFn: () => api<ServerMetricsSnapshot>('/monitoring/server'),
    enabled: serverMetricsEnabled,
    retry: false,
    refetchInterval: SERVER_REFRESH_INTERVAL_MS,
  });

  useEffect(() => {
    if (widgetsQuery.data) replaceWidgetDraft(widgetsQuery.data);
  }, [widgetsQuery.data]);

  useEffect(() => {
    window.localStorage.setItem('intez-dashboard-trend-period', trendPeriod);
  }, [trendPeriod]);

  const saveWidgets = useMutation({
    mutationFn: ({ widgets }: { widgets: DashboardWidgetPreference[]; source: 'settings' | 'layout' }) => api<DashboardWidgetPreference[]>('/dashboard/widgets', {
      method: 'PUT',
      body: JSON.stringify({ widgets }),
    }),
    onSuccess: (value, request) => {
      queryClient.setQueryData(widgetQueryKey, value);
      replaceWidgetDraft(value);
      if (request.source === 'settings') setWidgetSettingsOpen(false);
    },
  });

  const dashboardUser: DashboardUser = {
    userType: user?.userType || 'EMPLOYEE',
    role: user?.role || 'EXECUTOR',
    roles: user?.roles,
    fullName: user?.fullName || '',
  };
  const loading = overviewQuery.isPending || widgetsQuery.isPending;
  const failed = overviewQuery.isError || widgetsQuery.isError;
  const state: DashboardState = loading
    ? 'loading'
    : failed
      ? 'error'
      : 'ready';
  const queryError = overviewQuery.error || widgetsQuery.error;
  const displayedWidgets = widgetDraft.length ? widgetDraft : (widgetsQuery.data || []);
  const widgetSaveError = saveWidgets.error instanceof Error
    ? 'Не удалось сохранить расположение. Проверьте соединение и повторите попытку.'
    : undefined;

  return <DashboardView
    appearanceControls={<DashboardAppearance />}
    user={dashboardUser}
    state={state}
    data={overviewQuery.data}
    trend={trendQuery.data}
    trendPeriod={trendPeriod}
    trendLoading={trendQuery.isFetching}
    statusMetrics={statusMetricsQuery.data}
    statusPeriod={statusPeriod}
    statusLoading={statusMetricsQuery.isFetching}
    enabledWidgets={displayedWidgets.filter(item => item.enabled).map(item => item.key)}
    widgetSettingsOpen={widgetSettingsOpen}
    widgetDraft={widgetDraft}
    savingWidgets={saveWidgets.isPending}
    widgetsSaved={saveWidgets.isSuccess && saveWidgets.variables?.source === 'layout'}
    widgetSaveError={widgetSaveError}
    diagnostics={diagnosticsQuery.data}
    diagnosticsState={!diagnosticsEnabled ? 'loading' : diagnosticsQuery.isError ? 'error' : diagnosticsQuery.isPending ? 'loading' : diagnosticsQuery.data ? 'ready' : 'error'}
    monitoring={monitoringQuery.data}
    monitoringState={!monitoringEnabled ? 'loading' : monitoringQuery.isError ? 'error' : monitoringQuery.isPending ? 'loading' : monitoringQuery.data ? 'ready' : 'error'}
    serverMetrics={serverMetricsQuery.data}
    serverMetricsState={!serverMetricsEnabled ? 'loading' : serverMetricsQuery.isError ? 'error' : serverMetricsQuery.isPending ? 'loading' : serverMetricsQuery.data ? 'ready' : 'error'}
    errorMessage={queryError instanceof Error ? queryError.message : undefined}
    onNavigate={navigate}
    onRetry={() => { void overviewQuery.refetch(); void widgetsQuery.refetch(); }}
    onRetryDiagnostics={() => { void diagnosticsQuery.refetch(); }}
    onRetryMonitoring={() => { void monitoringQuery.refetch(); }}
    onRetryServerMetrics={() => { void serverMetricsQuery.refetch(); }}
    onOpenWidgetSettings={() => { saveWidgets.reset(); replaceWidgetDraft(widgetsQuery.data || []); setWidgetSettingsOpen(true); }}
    onCloseWidgetSettings={() => { saveWidgets.reset(); replaceWidgetDraft(widgetsQuery.data || []); setWidgetSettingsOpen(false); }}
    onPreviewWidgetMove={(key, targetKey) => {
      if (saveWidgets.isPending || key === targetKey) return;
      const current = widgetDraftRef.current;
      const sourceIndex = current.findIndex(item => item.key === key);
      const targetIndex = current.findIndex(item => item.key === targetKey);
      if (sourceIndex === targetIndex) return;
      if (targetIndex < 0) return;
      replaceWidgetDraft(moveDashboardWidget(current, key, targetIndex));
    }}
    onCommitWidgetMove={() => {
      const next = widgetDraftRef.current;
      const saved = widgetsQuery.data || [];
      const changed = next.length !== saved.length || next.some((item, index) => item.key !== saved[index]?.key || item.enabled !== saved[index]?.enabled);
      if (changed) saveWidgets.mutate({ widgets: next, source: 'layout' });
    }}
    onCancelWidgetMove={() => replaceWidgetDraft(widgetsQuery.data || [])}
    onRetryWidgetSave={() => saveWidgets.mutate({ widgets: widgetDraftRef.current, source: 'layout' })}
    onSaveWidgets={() => saveWidgets.mutate({ widgets: widgetDraftRef.current, source: 'settings' })}
    onWidgetDraftChange={replaceWidgetDraft}
    onTrendPeriodChange={setTrendPeriod}
    onStatusPeriodChange={setStatusPeriod}
  />;
}
